import { useState } from "react";
import { BrandMark } from "../components/BrandMark";
import { NpsChoice } from "../components/NpsChoice";
import { StarRating } from "../components/StarRating";
import { StepProgress } from "../components/StepProgress";
import { YesMaybeNo } from "../components/YesMaybeNo";
import {
  type DailyUse,
  type Nps,
  SAUCES,
  type SauceRating,
  type SauceType,
  type SurveyAnswers,
} from "../lib/types";

interface Props {
  sauceType: SauceType;
  onBack: () => void;
  onNext: (answers: SurveyAnswers) => void;
}

const TOTAL_STEPS = 3;

export function RateScreen({ sauceType, onBack, onNext }: Props) {
  const meta = SAUCES[sauceType];
  const [step, setStep] = useState(1);

  const [sauceRating, setSauceRating] = useState<SauceRating | null>(null);
  const [likedMost, setLikedMost] = useState("");
  const [dailyUse, setDailyUse] = useState<DailyUse | null>(null);
  const [dailyUseComment, setDailyUseComment] = useState("");
  const [nps, setNps] = useState<Nps | null>(null);

  const canAdvance = (() => {
    switch (step) {
      case 1:
        return sauceRating !== null;
      case 2:
        return dailyUse !== null;
      case 3:
        return nps !== null;
      default:
        return false;
    }
  })();

  const advance = () => {
    if (!canAdvance) return;
    if (step < TOTAL_STEPS) {
      setStep(step + 1);
      return;
    }
    if (sauceRating === null || dailyUse === null || nps === null) return;
    onNext({
      sauceType,
      sauceRating,
      likedMost: likedMost.trim() || null,
      dailyUse,
      dailyUseComment: dailyUseComment.trim() || null,
      nps,
    });
  };

  const back = () => {
    if (step > 1) {
      setStep(step - 1);
    } else {
      onBack();
    }
  };

  return (
    <main className="shell flex flex-col px-6 pb-8 pt-10">
      <header className="mb-6 flex justify-center">
        <BrandMark className="h-14 w-auto" />
      </header>

      <div className="mb-3">
        <SauceBadge name={meta.name} accent={meta.accent} />
      </div>

      <div className="mb-8">
        <StepProgress current={step} total={TOTAL_STEPS} />
      </div>

      <div className="flex flex-1 flex-col">
        {step === 1 && (
          <Step1Sauce
            sauceName={meta.name}
            rating={sauceRating}
            onRating={setSauceRating}
            liked={likedMost}
            onLiked={setLikedMost}
          />
        )}
        {step === 2 && (
          <Step2Daily
            choice={dailyUse}
            onChoice={setDailyUse}
            comment={dailyUseComment}
            onComment={setDailyUseComment}
          />
        )}
        {step === 3 && <Step3Nps sauceName={meta.name} value={nps} onChange={setNps} />}
      </div>

      <div className="mt-6 flex gap-3">
        <button type="button" onClick={back} className="pill-btn pill-btn-ghost flex-1">
          Back
        </button>
        <button
          type="button"
          disabled={!canAdvance}
          onClick={advance}
          className="pill-btn pill-btn-primary flex-1"
        >
          {step === TOTAL_STEPS ? "Submit" : "Next"}
        </button>
      </div>
    </main>
  );
}

function SauceBadge({ name, accent }: { name: string; accent: string }) {
  return (
    <div className="inline-flex items-center gap-2 rounded-full border border-cream/15 bg-charcoal-2 px-3 py-1.5 text-xs uppercase tracking-[0.2em] text-cream-dim">
      <span
        aria-hidden="true"
        className="h-2.5 w-2.5 rounded-full"
        style={{ backgroundColor: accent }}
      />
      Rating {name}
    </div>
  );
}

function StepHeading({ kicker, title }: { kicker: string; title: string }) {
  return (
    <div className="mb-6">
      <p className="mb-2 text-xs uppercase tracking-[0.3em] text-amber">{kicker}</p>
      <h1 className="text-3xl text-cream sm:text-4xl">{title}</h1>
    </div>
  );
}

function Step1Sauce({
  sauceName,
  rating,
  onRating,
  liked,
  onLiked,
}: {
  sauceName: string;
  rating: SauceRating | null;
  onRating: (n: SauceRating) => void;
  liked: string;
  onLiked: (s: string) => void;
}) {
  return (
    <div>
      <StepHeading kicker="The sauce" title={`How would you rate the ${sauceName} sauce?`} />
      <StarRating
        value={rating}
        onChange={(n) => onRating(n as SauceRating)}
        ariaLabel={`${sauceName} sauce rating, 1 to 5 stars`}
        lowLabel="Not for me"
        highLabel="Love it"
      />

      <div
        className={`mt-8 transition-opacity duration-300 ${
          rating === null ? "pointer-events-none opacity-0" : "opacity-100"
        }`}
      >
        <label
          htmlFor="liked"
          className="mb-2 block text-sm uppercase tracking-wider text-cream-dim"
        >
          What stood out?{" "}
          <span className="normal-case tracking-normal text-cream-dim/60">(optional)</span>
        </label>
        <textarea
          id="liked"
          value={liked}
          onChange={(e) => onLiked(e.target.value.slice(0, 500))}
          rows={3}
          placeholder="Smoke, salt, heat, sweetness…"
          className="field resize-none"
        />
      </div>
    </div>
  );
}

const DAILY_PLACEHOLDERS: Record<DailyUse, string> = {
  yes: "Wings, burgers, brisket, on everything…",
  maybe: "What would push it over the line?",
  no: "What turned you off?",
};

function Step2Daily({
  choice,
  onChoice,
  comment,
  onComment,
}: {
  choice: DailyUse | null;
  onChoice: (v: DailyUse) => void;
  comment: string;
  onComment: (s: string) => void;
}) {
  return (
    <div>
      <StepHeading
        kicker="Everyday use"
        title="Can you see yourself using this sauce in your daily routine?"
      />
      <YesMaybeNo value={choice} onChange={onChoice} />

      <div
        className={`mt-6 transition-opacity duration-300 ${
          choice === null ? "pointer-events-none opacity-0" : "opacity-100"
        }`}
      >
        <label
          htmlFor="daily-comment"
          className="mb-2 block text-sm uppercase tracking-wider text-cream-dim"
        >
          Tell us why{" "}
          <span className="normal-case tracking-normal text-cream-dim/60">(optional)</span>
        </label>
        <textarea
          id="daily-comment"
          value={comment}
          onChange={(e) => onComment(e.target.value.slice(0, 300))}
          rows={3}
          placeholder={choice ? DAILY_PLACEHOLDERS[choice] : ""}
          className="field resize-none"
        />
      </div>
    </div>
  );
}

function Step3Nps({
  sauceName,
  value,
  onChange,
}: {
  sauceName: string;
  value: Nps | null;
  onChange: (n: Nps) => void;
}) {
  return (
    <div>
      <StepHeading
        kicker="Spread the word"
        title={`How likely are you to recommend the ${sauceName} sauce to someone else?`}
      />
      <NpsChoice value={value} onChange={onChange} />
    </div>
  );
}
